/**
 * The AudioWorklet sample pump.
 *
 * Held as a source string rather than a separate file because an AudioWorklet is
 * loaded by URL, and a sibling file would have to be resolved relative to
 * `import.meta.url` — which works from the module tree but not from the
 * single-file bundle, and not at all if that bundle is loaded as CommonJS.
 * A Blob URL has no deployment surface at all.
 *
 * ── Why a worklet rather than AudioBufferSourceNode scheduling ──
 *
 * `playbackRate` cannot play a buffer backwards in Chromium, so jogging and
 * reverse are unreachable from the simple design. Here the read position is a
 * fractional cursor with a *signed* increment:
 *
 *     delta = speed × fileRate / contextRate
 *
 * so rate conversion, varispeed, jog, and reverse are all that one line. Because
 * af serves uncompressed float PCM, the samples arriving here are the file's own
 * samples — no decoder, no encoder delay, no segment seams to align.
 *
 * Interpolation kernels are compiled in from audio/interpolators/ by
 * stringifying them, so they stay ordinary testable functions on disk while
 * still reaching the worklet. All of them are present at once and selected by
 * id at runtime — switching is a message, not a reload, which is what makes an
 * A/B comparison instant.
 */

import { INTERPOLATOR_KERNELS } from "./interpolators/index.js";
import { TIMESTRETCH_FUNCTIONS } from "./timestretch/wsola.js";

const PROCESSOR_BODY = `
class DawPlaybackProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();

    const config = options.processorOptions || {};

    // Ring of source samples at the FILE's rate. It holds a window *around* the
    // read position, not ahead of it — reverse playback walks backwards and
    // needs the same margin behind as forward needs in front.
    this.capacity = config.capacity || 1 << 20;
    this.ring = new Float32Array(this.capacity);

    // Absolute source-sample index of ring[0 % capacity]. The ring is a sliding
    // window over the file, so an absolute index maps to a slot by modulo.
    this.ringStart = 0;
    this.ringEnd = 0; // exclusive

    this.position = config.position || 0; // fractional, absolute source samples
    this.speed = 0;                        // 0 = stopped
    this.rateRatio = config.rateRatio || 1; // fileRate / contextRate
    this.sampleCount = config.sampleCount || 0;

    this.underruns = 0;
    this.stalled = false;
    this.lastReport = -1;

    // Ramp speed changes over a few ms so a direction flip or a jog nudge
    // doesn't step the increment and click.
    this.targetSpeed = 0;
    // ~5 ms time constant regardless of the context rate. Fast enough that a
    // jog nudge feels immediate, slow enough that a direction flip doesn't step
    // the increment and click.
    this.glide = config.smoothing === false ? 1 : 1 / (0.005 * sampleRate);

    this.capture = null;
    this.captureAt = 0;
    this.captureRestore = null;

    // Interpolation. The window is assembled per output sample and clamped to
    // what the ring holds, so kernels are plain functions over plain arrays and
    // never have to think about wrapping or edges.
    this.kernel = KERNELS.linear;
    this.table = null;
    this.kernelParams = { taps: 2 };
    this.before = 0;
    this.after = 1;
    this.identityAtZero = true;
    this.window = new Float32Array(2);

    // Pitch preservation (WSOLA). Off unless the host asks for it. The stretcher
    // sits between the ring and the interpolator: it consumes source samples at
    // 'speed' and emits them at the file rate, which leaves the kernel doing
    // nothing but rateRatio and so keeps every kernel working unmodified.
    const fileRate = this.rateRatio * sampleRate;
    const geometry = stretchGeometry(fileRate);

    this.preservePitch = false;
    this.stretching = false;
    // Speeds between which pitch is held exactly. Outside them the stretch
    // factor clamps, so correction becomes partial rather than warbling.
    this.minPreserveSpeed = 0.5;
    this.maxPreserveSpeed = 2;
    this.frame = geometry.frame;
    this.hop = geometry.hop;
    this.search = geometry.search;
    this.hann = hannWindow(this.frame);
    this.template = new Float32Array(this.hop);
    this.candidates = new Float32Array(2 * this.search + this.hop);

    // Overlap-add accumulator, as a ring: comfortably more than the frame, so
    // the interpolator's taps can still reach back below the read cursor.
    this.synthCapacity = 1;
    while (this.synthCapacity < 6 * this.frame) this.synthCapacity <<= 1;
    this.synth = new Float32Array(this.synthCapacity);

    this.stretchRead = 0;  // fractional, absolute index into the stretched stream
    this.stretchReady = 0; // exclusive; stretched samples that are final
    this.stretchPrimed = false;
    this.stretchDirty = true;
    this.prevAnalysis = 0;

    this.port.onmessage = (event) => this.handleMessage(event.data);
  }

  handleMessage(message) {
    switch (message.type) {
      case 'samples': {
        // Blocks arrive as {start, data}; anything already passed is dropped.
        this.write(message.start, message.data);
        break;
      }
      case 'speed':
        // Only the target moves; process() glides the actual speed toward it.
        this.targetSpeed = message.value;
        break;
      case 'seek': {
        // Only discard what's held if the target isn't in it. Clicking around
        // inside buffered audio is the common case, and throwing the ring away
        // each time would refetch audio that never left memory.
        const target = Math.floor(message.position);
        if (!this.has(target)) {
          this.ringStart = this.ringEnd = target;
        }
        this.position = message.position;
        this.stalled = false;
        // Whatever is half-built in the overlap-add belongs to where we were.
        // Flag it rather than re-anchoring here: process() advances the cursor
        // by one delta before its first read, and the stretcher has to start
        // from that same sample or the track sits one output sample late.
        this.stretchDirty = true;
        break;
      }
      case 'pitch':
        this.preservePitch = !!message.value;
        break;
      case 'pitchRange':
        this.minPreserveSpeed = message.min;
        this.maxPreserveSpeed = message.max;
        break;
      case 'stop':
        this.targetSpeed = 0;
        this.speed = 0;
        break;
      case 'interpolator':
        this.kernel = KERNELS[message.kernel] || KERNELS.linear;
        this.table = message.table || null;
        this.kernelParams = message.params;
        this.before = message.before;
        this.after = message.after;
        this.identityAtZero = message.identityAtZero !== false;
        this.window = new Float32Array(message.before + message.after + 1);
        break;
      case 'capture':
        // Test hook: jump to an exact position at exactly unit speed and record
        // N output samples. Deterministic on purpose — measuring against live
        // playback would race the speed ramp and the fetch.
        if (message.position !== undefined) {
          // Remember where we were: a capture is a measurement, and leaving the
          // pump running from wherever it finished would have it free-run away
          // from the transport.
          this.captureRestore = {
            position: this.position,
            speed: this.speed,
            targetSpeed: this.targetSpeed,
          };
          this.position = message.position;
          this.speed = message.speed === undefined ? 1 : message.speed;
          this.targetSpeed = this.speed;
        }
        this.capture = new Float32Array(message.length);
        this.captureAt = 0;
        break;
    }
  }

  write(start, data) {
    const end = start + data.length;

    for (let i = 0; i < data.length; i++) {
      this.ring[(start + i) % this.capacity] = data[i];
    }

    if (this.ringEnd === this.ringStart) {
      // Empty, or just seeked.
      this.ringStart = start;
      this.ringEnd = end;
    } else if (start > this.ringEnd || end < this.ringStart) {
      // Disjoint from what's held — a jump. Anything else in the ring may have
      // been aliased over by this write, so the span becomes just this block.
      this.ringStart = start;
      this.ringEnd = end;
    } else {
      // Overlapping or adjacent on either side. Extending downward matters:
      // responses land out of order, and reverse playback fills downward the
      // whole time.
      if (start < this.ringStart) this.ringStart = start;
      if (end > this.ringEnd) this.ringEnd = end;
    }

    // Never claim more than the ring physically holds. Trim the side further
    // from the read position, so the audio about to be needed survives whichever
    // direction we're travelling.
    if (this.ringEnd - this.ringStart > this.capacity) {
      if (this.position - this.ringStart > this.ringEnd - this.position) {
        this.ringStart = this.ringEnd - this.capacity;
      } else {
        this.ringEnd = this.ringStart + this.capacity;
      }
    }
  }

  has(index) {
    return index >= this.ringStart && index < this.ringEnd;
  }

  sampleAt(index) {
    return this.ring[((index % this.capacity) + this.capacity) % this.capacity];
  }

  /**
   * Interpolate at a fractional position, or null if the pump has starved.
   *
   * Only the sample under the cursor decides whether we're starved; taps that
   * fall outside the ring are clamped to its edge. A kernel wanting 32 taps
   * shouldn't stall a track that has the audio it's actually playing.
   */
  read(position) {
    const base = Math.floor(position);
    if (!this.has(base)) return null;

    const fraction = position - base;
    // Exact sample hit. Only an interpolator can be skipped here; a filtering
    // kernel still has work to do. At 8 kHz into a 48 kHz context this lands on
    // one output sample in six, so getting it wrong is not a rare edge case.
    if (fraction === 0 && this.identityAtZero) return this.sampleAt(base);

    const before = this.before;
    const after = this.after;
    const lastHeld = this.ringEnd - 1;
    const end = this.sampleCount;

    for (let k = -before; k <= after; k++) {
      const index = base + k;
      let value;

      if (index < 0 || (end > 0 && index >= end)) {
        // Past the ends of the asset there genuinely is no audio, so the honest
        // answer is silence. Holding the first or last sample instead would put
        // a DC step under the kernel and ring on it.
        value = 0;
      } else if (index < this.ringStart) {
        // Inside the asset but not held: audio exists, we just haven't got it.
        // Holding the nearest sample is the better approximation, and in steady
        // state this never happens — the prefetch window is seconds deep against
        // a kernel that reaches a few milliseconds.
        value = this.sampleAt(this.ringStart);
      } else if (index > lastHeld) {
        value = this.sampleAt(lastHeld);
      } else {
        value = this.sampleAt(index);
      }

      this.window[before + k] = value;
    }

    return this.kernel(this.window, before, fraction, this.table, this.kernelParams);
  }

  // ---------------------------------------------------------- pitch ---

  /**
   * A source sample, with the same edge rules \`read\` uses: silence past the
   * ends of the asset, nearest held sample for audio that exists but hasn't
   * arrived.
   */
  inputAt(index) {
    if (index < 0 || (this.sampleCount > 0 && index >= this.sampleCount)) return 0;
    if (this.ringEnd <= this.ringStart) return 0;
    if (index < this.ringStart) return this.sampleAt(this.ringStart);
    if (index >= this.ringEnd) return this.sampleAt(this.ringEnd - 1);
    return this.sampleAt(index);
  }

  synthAt(index) {
    const capacity = this.synthCapacity;
    return this.synth[((index % capacity) + capacity) % capacity];
  }

  /**
   * How far to stretch, clamped.
   *
   * Ideally 1/speed, which holds pitch exactly. Beyond the configured range the
   * clamp takes over and the residual (alpha x speed) shifts the pitch by
   * whatever is left over. That's deliberately a *continuous* degradation: a
   * hard bypass at the boundary would click every time a jog crossed it, and
   * partial correction sounds better than the warble an unclamped ratio gives.
   */
  stretchFactor() {
    const magnitude = Math.abs(this.speed);
    if (magnitude < 1e-6) return 1;

    const lowest = 1 / this.maxPreserveSpeed;
    const highest = 1 / this.minPreserveSpeed;
    return Math.min(highest, Math.max(lowest, 1 / magnitude));
  }

  /** Drop the overlap-add and start it again from a transport position. */
  resetStretch(at) {
    this.synth.fill(0);
    // Start the read cursor on the *fractional* part, so stretched index 0 lines
    // up with the whole source sample below the transport. Rounding the origin
    // to a whole sample instead would put a sub-sample time offset on the track
    // every time this engaged — inaudible alone, but it means 1x is no longer
    // bit-identical to bypassing the stretcher, and two tracks toggling at
    // different moments would sit a fraction of a sample apart.
    this.stretchRead = at - Math.floor(at);
    this.stretchReady = 0;
    this.stretchPrimed = false;
    this.stretchDirty = false;
    this.prevAnalysis = Math.floor(at);

    // Prime the history below stretched index 0 with the samples that really do
    // precede the anchor. Without this the interpolator's left taps read zeros
    // for the first fraction of a millisecond after every seek and every toggle
    // — brief, but audible as a tick when scrubbing.
    const direction = this.speed < 0 ? -1 : 1;
    const anchor = Math.floor(at);
    for (let i = 1; i <= this.before + 2; i++) {
      const slot = ((-i % this.synthCapacity) + this.synthCapacity) % this.synthCapacity;
      this.synth[slot] = this.inputAt(anchor - direction * i);
    }
  }

  /**
   * Overlap-add one more frame, advancing the stretched stream by one hop.
   *
   * @param {number} position - the transport's current source position
   * @returns {boolean} false if the ring doesn't hold the frame yet
   */
  synthesize(position) {
    const frame = this.frame;
    const hop = this.hop;
    const search = this.search;

    // Reverse playback is the source traversed backwards — nothing more. Every
    // read below steps by \`direction\`, so the similarity search runs forwards
    // over a backwards view and never learns which way the transport is going.
    const direction = this.speed < 0 ? -1 : 1;
    const alpha = this.stretchFactor();

    // Where in the source the next stretched hop should come from. Derived from
    // the live transport every time rather than accumulated, so the analysis
    // pointer cannot drift away from the playhead however long this runs.
    const pending = this.stretchReady - this.stretchRead;
    const base = Math.round(position + (pending * direction) / alpha);
    const last = base + direction * (frame - 1);

    // Stall only for audio that exists and hasn't arrived. Past either end of
    // the asset there is nothing to wait for, so that reads as silence instead.
    const inAsset = (index) =>
      index >= 0 && (this.sampleCount === 0 || index < this.sampleCount);
    const reachable = (index) => !inAsset(index) || this.has(index);
    if (!reachable(base) || !reachable(last)) return false;

    let delta = 0;

    if (this.stretchPrimed) {
      // What would have followed the previous frame if we had simply read on.
      for (let i = 0; i < hop; i++) {
        this.template[i] = this.inputAt(this.prevAnalysis + direction * (hop + i));
      }

      const span = 2 * search + hop;
      for (let i = 0; i < span; i++) {
        this.candidates[i] = this.inputAt(base + direction * (i - search));
      }

      delta = bestOffset(this.candidates, this.template, hop, search);
    }

    const analysis = base + direction * delta;

    // The frame's second half lands on slots nothing has written yet.
    for (let i = hop; i < frame; i++) {
      this.synth[(this.stretchReady + i) % this.synthCapacity] = 0;
    }

    for (let i = 0; i < frame; i++) {
      // The very first frame has no predecessor to fade against, so its leading
      // half goes in flat — otherwise every seek fades in over half a frame.
      const gain = !this.stretchPrimed && i < hop ? 1 : this.hann[i];
      this.synth[(this.stretchReady + i) % this.synthCapacity] +=
        this.inputAt(analysis + direction * i) * gain;
    }

    this.prevAnalysis = analysis;
    this.stretchReady += hop;
    this.stretchPrimed = true;
    return true;
  }

  /**
   * One output sample from the stretched stream, or null if it has starved.
   *
   * The interpolator runs over the overlap-add accumulator instead of the ring,
   * at a cursor advancing by rateRatio alone — speed has already been spent on
   * the stretch.
   */
  readStretched(position) {
    const alpha = this.stretchFactor();

    while (this.stretchReady < Math.floor(this.stretchRead) + this.after + 2) {
      if (!this.synthesize(position)) return null;
    }

    const base = Math.floor(this.stretchRead);
    const fraction = this.stretchRead - base;

    let value;
    if (fraction === 0 && this.identityAtZero) {
      value = this.synthAt(base);
    } else {
      const before = this.before;
      for (let k = -before; k <= this.after; k++) {
        this.window[before + k] = this.synthAt(base + k);
      }
      value = this.kernel(this.window, before, fraction, this.table, this.kernelParams);
    }

    // Stretched samples consumed per output sample. With alpha at its ideal
    // 1/speed this is exactly rateRatio — pure rate conversion, pitch held. Where
    // the clamp has bitten, the excess is what shifts the pitch.
    this.stretchRead += this.rateRatio * alpha * Math.abs(this.speed);
    return value;
  }

  process(_inputs, outputs) {
    const output = outputs[0][0];
    if (!output) return true;

    const glide = this.glide;

    for (let i = 0; i < output.length; i++) {
      this.speed += (this.targetSpeed - this.speed) * glide;

      if (this.speed === 0 && this.targetSpeed === 0) {
        output[i] = 0;
        continue;
      }

      const delta = this.speed * this.rateRatio;
      const next = this.position + delta;

      // Off either end of the asset: silence, and hold still.
      if (next < 0 || (this.sampleCount > 0 && next >= this.sampleCount)) {
        output[i] = 0;
        this.position = next < 0 ? 0 : this.sampleCount;
        continue;
      }

      // A stopped transport has no ratio to stretch by. Reverse is fine — the
      // stretcher traverses the source backwards.
      const preserve = this.preservePitch && Math.abs(this.speed) > 0.02;
      if (preserve !== this.stretching) {
        this.stretching = preserve;
        this.stretchDirty = true;
      }
      if (preserve && this.stretchDirty) this.resetStretch(next);

      const value = preserve ? this.readStretched(next) : this.read(next);
      if (value === null) {
        // Starved. Output silence and hold position — never skip forward, which
        // would silently desynchronise this track from the others.
        output[i] = 0;
        if (!this.stalled) {
          this.stalled = true;
          this.underruns++;
        }
        continue;
      }

      this.stalled = false;
      this.position = next;
      output[i] = value;

      if (this.capture && this.captureAt < this.capture.length) {
        this.capture[this.captureAt++] = value;
      }
    }

    if (this.capture && this.captureAt >= this.capture.length) {
      this.port.postMessage({ type: 'capture', data: this.capture }, [this.capture.buffer]);
      this.capture = null;

      if (this.captureRestore) {
        this.position = this.captureRestore.position;
        this.speed = this.captureRestore.speed;
        this.targetSpeed = this.captureRestore.targetSpeed;
        this.captureRestore = null;
      }
    }

    // Report roughly every 10 ms; the main thread drives the playhead from this
    // rather than from currentTime, so a stall shows as a stalled playhead
    // instead of one running ahead of audible sound.
    if (currentTime - this.lastReport > 0.01) {
      this.lastReport = currentTime;
      this.port.postMessage({
        type: 'position',
        position: this.position,
        speed: this.speed,
        stalled: this.stalled,
        underruns: this.underruns,
        bufferedStart: this.ringStart,
        bufferedEnd: this.ringEnd,
      });
    }

    return true;
  }
}

registerProcessor('daw-playback', DawPlaybackProcessor);
`;

/**
 * Kernels serialised into the worklet's scope.
 *
 * `Function.prototype.toString()` returns the original source, so the kernels
 * that run in the audio thread are the same code the Node tests exercise — no
 * second copy to keep in step.
 */
function buildProcessorSource() {
  const kernels = Object.entries(INTERPOLATOR_KERNELS)
    .map(([name, fn]) => `  ${name}: ${fn.toString()}`)
    .join(",\n");

  // Time-stretch helpers come across the same way and under the same rule: no
  // closures, no imports, everything through the arguments.
  const timestretch = Object.values(TIMESTRETCH_FUNCTIONS)
    .map((fn) => fn.toString())
    .join("\n");

  return `const KERNELS = {\n${kernels}\n};\n${timestretch}\n${PROCESSOR_BODY}`;
}

let cachedUrl = null;

/** Blob URL for the processor module, created once per page. */
export function playbackWorkletUrl() {
  if (!cachedUrl) {
    const source = buildProcessorSource();
    cachedUrl = URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
  }
  return cachedUrl;
}

export { buildProcessorSource };
